import {createSelector} from 'reselect'
import {convertData2JsonForm} from '../../../../shared/lib/convertData2JsonForm'
import {objectMap} from '../../../../../lib/utils/get-table-transform-schema-and-form-data'
import {addEnumValBySchema} from '../../../../shared/lib/addEnumValBySchema'
import { UserRoles } from '../../../../constants/roles'
import { 
    selectTimelineFiles as selectAllTimelineFiles,
    selectTimelineData as selectAllTimelineData 
} from '../../../../components/v2/activity-timeline/selectors'


export const createSession = createSelector(
    (state: any) => state.session,
    sessionData => sessionData
)
const selectMontage = state => state.montage

const selectMontageForm = createSelector(
    selectMontage,
    ({form}) => form
)

const selectMontageEnums = createSelector(
    selectMontage,
    ({enums}) => enums
)

export const getMontageId = createSelector(
    selectMontage,
    ({id}) => id
)

export const getMontageFormLoading = createSelector(
    selectMontageForm,
    ({isLoading}) => isLoading
)

export const getMontageFormError = createSelector(
    selectMontageForm,
    ({error}) => error
)

export const getMontageFormData = createSelector(
    selectMontageForm,
    ({data}) => data
)

export const getMontageStageCurrent = createSelector(
    getMontageFormData,
    (formData) => formData?.stages?.current
)

export const getMontageEnumsData = createSelector(
    selectMontageEnums,
    ({data}) => data
)

export const getMontageEnumsError = createSelector(
    selectMontageEnums,
    ({error}) => error
)

export const getMontageEnumsIsLoading = createSelector(
    selectMontageEnums,
    ({isLoading}) => isLoading
)

const selectChatAvito = state => state.chatAvito

export const selectChatAvitoIsLoading = createSelector(
  selectChatAvito,
  ({isLoading}) => isLoading
)

export const selectChatAvitoData = createSelector(
  selectChatAvito,
  ({data}) => data
)

export const selectChatAvitoError = createSelector(
  selectChatAvito,
  ({error}) => error
)

export const getActiveAvitoChatId = createSelector(
  selectChatAvito,
  ({activeChatId}) => activeChatId
)

export const getActiveAvitoChat = createSelector(
  [selectChatAvitoData, getActiveAvitoChatId],
  (data, activeChatId) => {
      return data?.filter((chatItem) => chatItem.chat_id === activeChatId)[0]
  }
)

export const getVisibleFormData = createSelector(
    [getMontageFormData, getMontageEnumsData],
    (formData, enums) => {
        if (formData && enums) {
            const { data: form, customer_id } = formData

            if (!form) {
                return false
            }

            const {
                resultObject,
                resultSchema
            } = convertData2JsonForm(form)

            const enumItems = enums?.['enum'] || []
            if (enums?.['city']) {
                enumItems.city_id = Object.values(objectMap(enums['city'], ([key, value]) => {
                    return [key, {id: parseInt(key), value: value}]
                }))
            }

            if (enums?.['region']) {
                enumItems.region_id = Object.values(objectMap(enums['region'], ([key, value]) => {
                    return [key, {id: parseInt(key), value: value}]
                }))
            }

            Object.keys(enumItems).forEach(code => {
                if (
                    resultSchema?.properties.hasOwnProperty(code)
                ) {
                    const enumValues = enums?.['enum']?.[code] || {}
                    const oneOf: Record<string, string>[] = []

                    Object.keys(enumValues).forEach(enumValue => {
                        return oneOf.push({
                            'const': enumValues[enumValue].id,
                            'title': enumValues[enumValue].value
                        })
                    })

                    if (resultSchema?.properties.hasOwnProperty(code)) {
                        addEnumValBySchema(resultSchema, code, oneOf, enumValues)
                    }

                    if (resultObject.hasOwnProperty(code)) {
                        resultObject[code] = resultObject[code] ? parseInt(resultObject[code]) : null
                    }
                }
            })
            if (resultSchema?.properties.hasOwnProperty('phone')) {
                resultSchema.properties['phone'] = {
                    ...resultSchema.properties['phone'],
                    options: {
                        ...resultSchema.properties['phone'].options,
                        customer_id,
                    },
                }
            }

            return { resultSchema, resultObject }
        }
    }
)

const getMontageFileIds = createSelector(
    getMontageFormData,
    (formData) => {
        if (!formData?.data) return []
        
        const fileIds: number[] = []
        
        const processField = (field) => {
            if (Array.isArray(field.value) && field.value.length > 0) {
                field.value.forEach((file) => {
                    if (file?.id && typeof file.id === 'number') {
                        fileIds.push(file.id)
                    }
                })
            }
        }
        
        formData.data.forEach((item) => {
            if (item.fields) {
                item.fields.forEach(processField)
            } else {
                processField(item)
            }
        })
        
        return fileIds
    }
)

export const selectFilteredTimelineFiles = createSelector(
    [selectAllTimelineFiles, getMontageFileIds, createSession],
    (timelineFiles, montageFileIds, session) => {
        const isInstaller = session?.sessionData?.roles?.includes(UserRoles.INSTALLER) || 
                           session?.sessionData?.roles?.includes(UserRoles.INSTALLATION_FOREMAN)

        const isSalesManager = session?.sessionData?.roles?.includes(UserRoles.SALES_MANAGER)
        
        if (!isInstaller || isSalesManager) {
            return timelineFiles
        }
        
        if (!montageFileIds || montageFileIds.length === 0) {
            return []
        }
        
        return timelineFiles?.map(activityItems => {
            const filteredFiles = activityItems?.items?.filter(item => {
                return item.type === 'file' && item.data?.id && montageFileIds.includes(item.data.id)
            })
            return filteredFiles?.length ? { ...activityItems, items: filteredFiles } : null
        }).filter((item): item is NonNullable<typeof item> => item !== null)
    }
)

export const selectFilteredTimelineData = createSelector(
    [selectAllTimelineData, getMontageFileIds, createSession],
    (timelineData, montageFileIds, session) => {
        const isInstaller = session?.sessionData?.roles?.includes(UserRoles.INSTALLER) || 
                           session?.sessionData?.roles?.includes(UserRoles.INSTALLATION_FOREMAN)
        const isSalesManager = session?.sessionData?.roles?.includes(UserRoles.SALES_MANAGER)
        
        if (!isInstaller || isSalesManager) {
            return timelineData
        }
        
        if (!timelineData?.items || !montageFileIds || montageFileIds.length === 0) {
            return {
                ...timelineData,
                items: timelineData?.items?.map(activityItems => ({
                    ...activityItems,
                    items: activityItems?.items?.filter(item => item.type !== 'file')
                })).filter(activityItems => activityItems?.items?.length > 0)
            }
        }
        
        return {
            ...timelineData,
            items: timelineData?.items?.map(activityItems => ({
                ...activityItems,
                items: activityItems?.items?.filter(item => {
                    if (item.type === 'file') {
                        return item.data?.id && montageFileIds.includes(item.data.id)
                    }
                    return true
                })
            })).filter(activityItems => activityItems?.items?.length > 0)
        }
    }
)
